// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Star, Users, Shield, Heart, Brain, Target, TrendingUp, Play, Crown, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import PricingPlans from '../components/subscription/PricingPlans.js';

// On-brand senior-fitness photography, matching the sales page. These are
// known-good, publicly delivered Cloudinary assets already used by the
// workout data (src/data/workouts/elite.js & premium.js).
const IMG_HERO =
  'https://res.cloudinary.com/dpy2y99bz/image/upload/f_auto,q_auto/v1762694651/image_8_ws2tup.jpg';
const IMG_LIFESTYLE_BAND =
  'https://res.cloudinary.com/dpy2y99bz/image/upload/v1762733339/image_uf10ly.jpg';

const Home = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="home-page min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
      {/* Hero Section - Enhanced with gradient */}
      <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full -ml-36 -mb-36"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold sm:text-6xl lg:text-7xl mb-6 drop-shadow-lg">
                Fitness Designed for Seniors
              </h1>
              <p className="mt-4 text-xl lg:text-2xl text-indigo-100 leading-relaxed">
                ElderFit Secrets provides safe, effective workouts tailored specifically 
                for seniors. Stay active, independent, and healthy with our expertly designed 
                exercise programs.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                {isAuthenticated ? (
                  <Link to="/dashboard" className="inline-flex items-center justify-center px-8 py-4 bg-white text-indigo-600 hover:bg-indigo-50 font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                    Go to Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className="inline-flex items-center justify-center px-8 py-4 bg-white text-indigo-600 hover:bg-indigo-50 font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                      Start For Free
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                    <Link to="/login" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white hover:bg-white/20 font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                      Log In
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="mt-10 lg:mt-0">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src={IMG_HERO}
                  alt="An active older adult smiling while exercising gently at home"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/60 via-indigo-900/15 to-transparent" />
                <div className="relative px-5 py-10 sm:px-6 sm:py-12 text-white [text-shadow:_0_2px_6px_rgb(0_0_0_/_90%),_0_1px_2px_rgb(0_0_0_/_80%)]">
                  <h3 className="text-3xl font-extrabold mb-6">Why ElderFit Secrets?</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 bg-orange-500 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="ml-2 font-semibold text-white whitespace-nowrap text-[3.6vw] sm:text-lg lg:text-xl">Exercises designed specifically for senior mobility</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 bg-orange-500 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="ml-2 font-semibold text-white whitespace-nowrap text-[3.6vw] sm:text-lg lg:text-xl">Safety features with emergency contact integration</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 bg-orange-500 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="ml-2 font-semibold text-white whitespace-nowrap text-[3.6vw] sm:text-lg lg:text-xl">Progress tracking designed for realistic goals</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 bg-orange-500 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="ml-2 font-semibold text-white whitespace-nowrap text-[3.6vw] sm:text-lg lg:text-xl">Family monitoring dashboard for peace of mind</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 bg-orange-500 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="ml-2 font-semibold text-white whitespace-nowrap text-[3.6vw] sm:text-lg lg:text-xl">AI-powered guidance with real-time feedback</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Key Features</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for a safe and effective fitness journey
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg">
                  <Shield className="h-8 w-8" />
                </div>
              </div>
              <h4 className="text-2xl font-bold text-center mb-4 text-gray-900">Safe Workouts</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Exercises designed with senior safety in mind. Options for all mobility levels with 
                clear instructions and safety tips.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg">
                  <TrendingUp className="h-8 w-8" />
                </div>
              </div>
              <h4 className="text-2xl font-bold text-center mb-4 text-gray-900">Progress Tracking</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Monitor your fitness journey with easy-to-use tracking tools designed specifically 
                for seniors' goals and milestones.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg">
                  <Users className="h-8 w-8" />
                </div>
              </div>
              <h4 className="text-2xl font-bold text-center mb-4 text-gray-900">Family Connection</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Share your activity with family members for support and accountability. Emergency 
                contact integration for peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle image band */}
      <section className="pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
            <img
              src={IMG_LIFESTYLE_BAND}
              alt="Active adults over 50 staying strong and moving with confidence"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/85 via-indigo-900/60 to-indigo-900/20" />
            <div className="relative px-6 py-14 sm:px-10 md:px-14 md:py-20 max-w-2xl text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5 text-sm font-medium">
                <Heart className="w-4 h-4 text-indigo-100" />
                Made for healthy aging
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Built for real people over 50 — at your own pace
              </h2>
              <p className="text-lg text-indigo-100 mb-6">
                Whether you're just getting back into movement or staying sharp for the
                grandkids, every routine meets you exactly where you are — safe, gentle,
                and genuinely enjoyable.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-300" /> Beginners welcome
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-300" /> Low-impact &amp; joint-friendly
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-300" /> Do it from home
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section - Using PricingPlans component - Enhanced */}
      <section id="pricing" className="py-16 lg:py-20 bg-gradient-to-br from-gray-100 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Choose Your Perfect Plan</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start your fitness journey with a plan that fits your needs
            </p>
          </div>
          {/* Integrate the PricingPlans component */}
          <PricingPlans isHomePage={true} />
        </div>
      </section>

      {/* Testimonials Section - Enhanced */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What Our Members Say</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real stories from real people on their fitness journey
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="flex text-yellow-400 mb-6 text-xl">
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg italic">
                "I've tried many exercise programs, but ElderFit Secrets is the first one that truly understands 
                the needs of seniors. I feel stronger and more confident!"
              </p>
              <div className="border-t border-gray-200 pt-6">
                <h5 className="font-bold text-gray-900 text-lg">Martha J., 72</h5>
                <p className="text-sm text-gray-500 mt-1">Member for 8 months</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="flex text-yellow-400 mb-6 text-xl">
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg italic">
                "The family dashboard gives my children peace of mind, and I love that I can track my progress. 
                The exercises are perfect for my mobility level."
              </p>
              <div className="border-t border-gray-200 pt-6">
                <h5 className="font-bold text-gray-900 text-lg">Robert T., 68</h5>
                <p className="text-sm text-gray-500 mt-1">Member for 1 year</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="flex text-yellow-400 mb-6 text-xl">
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg italic">
                "After my hip replacement, I was afraid to exercise. The safety features and 
                modifications have helped me regain strength without worry."
              </p>
              <div className="border-t border-gray-200 pt-6">
                <h5 className="font-bold text-gray-900 text-lg">Diane M., 75</h5>
                <p className="text-sm text-gray-500 mt-1">Member for 6 months</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full -ml-36 -mb-36"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 drop-shadow-lg">Start Your Fitness Journey Today</h2>
          <p className="text-xl lg:text-2xl mb-10 max-w-3xl mx-auto text-indigo-100">Join thousands of seniors improving their strength, balance, and overall health.</p>
          <Link to="/register" className="inline-flex items-center justify-center py-4 px-10 bg-white text-indigo-600 font-bold text-lg rounded-lg shadow-2xl hover:shadow-3xl hover:bg-indigo-50 transition-all duration-300 transform hover:-translate-y-1">
            Start For Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;